import { useState, useCallback, useEffect } from "react";
import type { ColumnPinningState, OnChangeFn } from "@tanstack/react-table";

import {
  getStorageKey,
  readFromLocalStorage,
  writeToLocalStorage,
} from "../utils";

interface UseColumnPinningOptions {
  enabled?: boolean;
  columnPinning?: ColumnPinningState;
  onColumnPinningChange?: OnChangeFn<ColumnPinningState>;
  localStorageKeyPrefix?: string;
  initialPinnedColumns?: string[];
}

export const useColumnPinning = ({
  enabled = true,
  columnPinning: controlledPinning,
  onColumnPinningChange: controlledOnChange,
  localStorageKeyPrefix,
  initialPinnedColumns = [],
}: UseColumnPinningOptions) => {
  const isControlled = controlledPinning !== undefined;
  const storageKey = getStorageKey(localStorageKeyPrefix);

  const [internalPinning, setInternalPinning] = useState<ColumnPinningState>(
    () => {
      if (!enabled) return { left: [], right: [] };

      const stored = readFromLocalStorage(storageKey);
      const left = stored.length > 0 ? stored : initialPinnedColumns;

      return { left, right: [] };
    }
  );

  const pinning = isControlled ? controlledPinning : internalPinning;

  const onColumnPinningChange: OnChangeFn<ColumnPinningState> = useCallback(
    updaterOrValue => {
      const newPinning =
        typeof updaterOrValue === "function"
          ? updaterOrValue(pinning)
          : updaterOrValue;

      if (!isControlled) {
        setInternalPinning(newPinning);
      }

      controlledOnChange?.(updaterOrValue);

      if (enabled) {
        writeToLocalStorage(storageKey, newPinning.left ?? []);
      }
    },
    [pinning, isControlled, controlledOnChange, enabled, storageKey]
  );

  const toggleColumnPin = useCallback(
    (columnId: string) => {
      onColumnPinningChange(old => {
        const leftPins = old.left ?? [];
        const isPinned = leftPins.includes(columnId);
        const newLeft = isPinned
          ? leftPins.filter(id => id !== columnId)
          : [...leftPins, columnId];

        return { ...old, left: newLeft };
      });
    },
    [onColumnPinningChange]
  );

  const isColumnPinned = useCallback(
    (columnId: string): boolean => {
      return (pinning.left ?? []).includes(columnId);
    },
    [pinning]
  );

  useEffect(() => {
    if (!enabled || isControlled) return;

    const stored = readFromLocalStorage(storageKey);
    if (stored.length > 0) {
      setInternalPinning({ left: stored, right: [] });
    }
  }, [enabled, isControlled, storageKey]);

  return {
    columnPinning: enabled ? pinning : { left: [], right: [] },
    onColumnPinningChange,
    toggleColumnPin,
    isColumnPinned,
  };
};
